import { debounce } from '@utilities/theme';

/**
 * layout-height-observer.js — site-wide ES module (loaded via <script type="module">).
 *
 * Tracks the rendered heights of #site-header and #site-footer and exposes them as
 * CSS custom properties (--header-height, --footer-height) on document.body, so layout
 * across the theme (drawers, facets, heroes, scroll offsets) can position against the
 * live header/footer size.
 *
 * #site-header wraps the full top stack (app banner + announcement bar + header group),
 * so --header-height reflects the whole stack and updates automatically when the app
 * banner collapses/expands, on font load, and on resize — no scroll polling needed.
 *
 * Public contracts consumed elsewhere (do not change without updating consumers):
 *   - body CSS vars --header-height / --footer-height
 *   - `header-height:change` CustomEvent ({ detail: { height } })
 *   - window.theme.header.height  (theme.header is seeded in theme-utilities.liquid)
 *
 * Extracted from the former global.js during the effort to remove that classic script.
 */

/**
 * Set a body-level height CSS variable, dispatching header-height:change and updating
 * window.theme.header.height when the header variable changes.
 * @param {string} propertyName - CSS custom property name (e.g. '--header-height')
 * @param {string} newValue - New value including unit (e.g. '120px')
 */
function updateHeightVariable(propertyName, newValue) {
  const currentValue = document.body.style.getPropertyValue(propertyName);
  if (currentValue === newValue) return;
  document.body.style.setProperty(propertyName, newValue);
  if (propertyName === '--header-height') {
    const parsedHeight = parseFloat(newValue) || 0;
    if (window.theme && window.theme.header) {
      window.theme.header.height = parsedHeight;
    }
    document.dispatchEvent(new CustomEvent('header-height:change', {
      detail: { height: parsedHeight }
    }));
  }
}

/**
 * Measure the header stack's rendered height.
 *
 * offsetHeight alone under-reports here. On the homepage the header can be
 * transparent, and base.css then sets .section-header min-height:0 and
 * .header-wrapper position:absolute so the hero slides up underneath it. That
 * takes the header row out of flow, so it stops contributing to #site-header's
 * offsetHeight and the variable collapses to roughly the announcement bar --
 * leaving everything anchored to --header-height (predictive search panel, menu
 * drawer backdrop, message tags) sitting behind the floating header.
 *
 * So measure to whichever sits further down the page: the element's own bottom
 * edge, or the header wrapper's. The wrapper reports its real position once
 * absolute, while
 * every ancestor between it and #site-header collapses to zero -- which is why
 * this looks at the wrapper directly rather than scanning immediate children.
 * Keyed off measured geometry rather than the transparent-header body class, so
 * it stays correct whatever takes the header out of flow, and it reduces to
 * plain offsetHeight when nothing does.
 *
 * @param {HTMLElement} element - The #site-header element
 * @returns {number} Height in pixels
 */
function measureHeaderHeight(element) {
  const top = element.getBoundingClientRect().top;
  let bottom = top + element.offsetHeight;

  const wrapper = element.querySelector('.header-wrapper');
  if (wrapper) {
    const rect = wrapper.getBoundingClientRect();
    // A hidden element reports an empty rect; ignore it rather than measure to it.
    if ((rect.height !== 0 || rect.width !== 0) && rect.bottom > bottom) {
      bottom = rect.bottom;
    }
  }

  return bottom - top;
}

/**
 * Measure an element's current height and write it to a body CSS variable.
 * @param {string} elementId - Element id to measure
 * @param {string} propertyName - CSS custom property to set
 */
function setElementHeightVariable(elementId, propertyName) {
  const element = document.getElementById(elementId);
  if (!element) return;
  const height = propertyName === '--header-height'
    ? measureHeaderHeight(element)
    : element.offsetHeight;
  updateHeightVariable(propertyName, `${height}px`);
}

function updateAllHeightVariables() {
  setElementHeightVariable('site-header', '--header-height');
  setElementHeightVariable('site-footer', '--footer-height');
}

/**
 * ResizeObserver-based height tracking for dynamic elements.
 * More efficient than scroll-based polling - only fires when dimensions actually change.
 * Automatically handles:
 * - App banner collapse/expand on scroll (CSS Grid changes height)
 * - Window resize
 * - Dynamic content changes
 * - Font loading affecting layout
 */
const heightObserverCache = new Map();

function observeElementHeight(elementId, propertyName) {
  const element = document.getElementById(elementId);
  if (!element) return;

  // Initial measurement
  setElementHeightVariable(elementId, propertyName);

  // Re-measure the element rather than trusting the entry's own blockSize: for
  // the header the observed box can be the collapsed one (see
  // measureHeaderHeight), so blockSize is exactly the wrong number.
  const observer = new ResizeObserver(() => {
    setElementHeightVariable(elementId, propertyName);
  });

  observer.observe(element);

  // An out-of-flow header row no longer resizes #site-header, so watching only
  // the parent would miss the header changing height (font load, quick nav,
  // breakpoint). Observe the wrapper too; the callback re-measures the whole
  // stack, and updateHeightVariable drops writes that don't change the value.
  if (propertyName === '--header-height') {
    const wrapper = element.querySelector('.header-wrapper');
    if (wrapper) observer.observe(wrapper);
  }

  heightObserverCache.set(elementId, observer);

  return observer;
}

// Initialize height observers after DOM is ready
// Use requestIdleCallback for non-blocking initialization, fallback to setTimeout
const initHeightObservers = () => {
  observeElementHeight('site-header', '--header-height');
  observeElementHeight('site-footer', '--footer-height');
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(initHeightObservers, { timeout: 1000 });
} else {
  setTimeout(initHeightObservers, 500);
}

// Keep window resize listener as fallback for elements not observed
// and for any other components that might depend on resize events
const debouncedUpdateHeightVariables = debounce(updateAllHeightVariables, 250);
window.addEventListener('resize', debouncedUpdateHeightVariables);
